import { VerificationResultText } from '@app/domain/accountVerification';
import { AggregatedValidatedPayeeServerResponse } from '@app/domain/aggregatedValidatedPayee';
import { StrongLegalIdentifies } from '@app/domain/legalEntityIdentifier';
import { SanctionsScreeningInfo, ValidatedPayeePrimaryFields, ValidatedPayeeServerResponse } from '@app/domain/validatedPayee';
import {
  ValidatedAccountEventData,
  ValidatedPayeeAccountAttachment,
  ValidatedPayeeAccountEventDataServerResponse,
  ValidatedPayeeAccountServerResponse,
} from '@app/domain/validatedPayeeAccount';
import ExtendedValidationLogo from '@app/images/validateResult/icons/extended.svg';
import SelfValidationLogo from '@app/images/validateResult/icons/self.svg';
import StandardValidationLogo from '@app/images/validateResult/icons/standard.svg';
import UnknownValidationLogo from '@app/images/validateResult/icons/unknown.svg';
import { UnifiedPayeeIds, UnifiedPayeeLegalEntityIdentifier } from '@mortee/domain/morteeAccountVerificationUnifiedPayee';
import { PrivatePayeeServerResponse } from '@mortee/domain/privatePayee';
import { PrivatePayeeAccountServerResponse } from '@mortee/domain/privatePayeeAccount';

export interface AccountVerificationPayeeSearchServerResponse {
  privateRecords?: PrivatePayeeServerResponse[];
  validatedPayeeData?: AggregatedValidatedPayeeServerResponse | null;
}

export interface MorteeVerificationServerResponse {
  validatedPayee: ValidatedPayeeServerResponse;
  validatedPayeeAccount: ValidatedPayeeAccountServerResponse;
  privatePayeeAccount: PrivatePayeeAccountServerResponse;
  validationData: VerificationDataServerResponse;
  aggregatedValidatedPayeeData: AggregatedValidatedPayeeServerResponse;
  lastValidatedPayeeAccountEventData: ValidatedAccountEventData | undefined;
}

export interface SearchServerResponse {
  id: string;
  createTimestamp: number;
  searchRequest: SearchRequest;
  score: SergeeSearchScore;
  status: SearchStatusType;
  metricsResult: PayeeMetricsResults;
  quickCheckId: string;
  validatedPayeeUniformId: string;
  validatingUser?: string;
  validatedTimestamp?: number;
  knoxId?: string;
  matchingValidationRecords?: MatchingValidationRecordServerResponse[];
}

export interface MatchingValidationRecordServerResponse {
  staticId: string;
  supplierName: string;
}

export interface PayeeMetricsResults {
  name: MetricResponse;
  countryCode: MetricResponse;
  taxId: MetricResponse;
  bankCode: MetricResponse;
  branchCode: MetricResponse;
  accountNumber: MetricResponse;
  address: MetricResponse;
  swiftCode: MetricResponse;
  duns: MetricResponse;
  iban: MetricResponse;
  bankName: MetricResponse;
  accountCountry: MetricResponse;
  payeeCountry: MetricResponse;
  additionalAddress: string[];
  additionalNames: string[];
  additionalTaxIds: string[];
  additionalIds: string[];
}

export interface UIMetricsResult {
  name: UIMetricResponse;
  countryCode: UIMetricResponse;
  taxId: UIMetricResponse;
  bankCode: UIMetricResponse;
  branchCode: UIMetricResponse;
  accountNumber: UIMetricResponse;
  address: UIMetricResponse;
  swiftCode: UIMetricResponse;
  duns: UIMetricResponse;
  iban: UIMetricResponse;
  bankName: UIMetricResponse;
  accountCountry: UIMetricResponse;
  payeeCountry: UIMetricResponse;
  additionalNames: UIMetricResponse;
  additionalIds: UIMetricResponse;
}

export interface UIMetricResponse {
  value: string[];
  result: SergeeMetricResult;
}

export interface MetricResponse {
  value: string;
  result: SergeeMetricResult;
}

export enum SergeeMetricResult {
  Match = 'Match',
  KnoxValidationRequired = 'KnoxValidationRequired',
  Mismatch = 'Mismatch',
  Unknown = 'Unknown',
  NotRelevant = 'NotRelevant',
  DoNotShow = 'DoNotShow',
  PartialMatch = 'PartialMatch',
}

export enum SergeeSearchScore {
  AAA = 'AAA',
  AA = 'AA',
  A = 'A',
  Unknown = 'Unknown',
  NotValidated = 'NotValidated',
  NotRelevant = 'NotRelevant',
}

export enum SearchStatusType {
  KnoxValidated = 'KnoxValidated',
  QuickCheckValidated = 'QuickCheckValidated',
  QuickCheckNoPermission = 'QuickCheckNoPermission',
  QuickCheckUnsupportedCountry = 'QuickCheckUnsupportedCountry',
  QuickCheckMissingDomesticAccount = 'QuickCheckMissingDomesticAccount',
  QuickCheckMissingIban = 'QuickCheckMissingIban',
  QuickCheckMissingTaxId = 'QuickCheckMissingTaxId',
  QuickCheckFailedRestCall = 'QuickCheckFailedRestCall',
  QuickCheckMatch = 'QuickCheckMatch',
  QuickCheckNoMatch = 'QuickCheckNoMatch',
  QuickCheckUnknown = 'QuickCheckUnknown',
  Error = 'Error',
  SelfValidated = 'SelfValidated', // self validated
}

export interface SearchRequest {
  payee: {
    name: string;
    ids: UnifiedPayeeIds;
    validatedPayeeUniformId: string;
  };
  account: Partial<MorteeAccountDetailsExtended>;
}

export interface VerificationDataServerResponse {
  result: VerificationResultType;
}

export interface MorteeVerificationResult {
  data?: ValidatedPayeeAccountEventDataServerResponse;
  result: VerificationResultType;
  privateAccountUniformId?: string;
  validatedAccountUniformId?: string;
  validatedPayeeLegalIdentifiers?: UnifiedPayeeLegalEntityIdentifier[];
  strongLegalIdentifiers?: StrongLegalIdentifies;
  primaryFields?: ValidatedPayeePrimaryFields | null;
  verificationAccountDetails?: Partial<MorteeAccountDetailsExtended> | null;
  validatedAccountAttachments?: ValidatedPayeeAccountAttachment[] | undefined;
  sanctionsScreeningInfo?: SanctionsScreeningInfo | null;
  verificationTimestamp?: number | null;
  presentationAccountNumber?: string | null;
}

export interface SergeeSearchResult {
  id: string;
  validatedTimestamp?: number;
  createTimestamp: number;
  searchRequest: SearchRequest;
  score: SergeeSearchScore;
  status: SearchStatusType;
  metricsResult: Partial<UIMetricsResult>;
  quickCheckId: string;
  validatedPayeeUniformId: string;
  validatingUser?: string;
  knoxId?: string;
  matchingValidationRecords: MatchingValidationRecordServerResponse[] | null;
}

export enum VerificationResultType {
  notValidated = 'NotValidated',
  selfApproved = 'SelfApproved',
  requireStandardValidation = 'RequireStandardValidation',
  requireExtendedValidation = 'RequireExtendedValidation',
  lightweightValidated = 'LightweightValidated',
  internalValidated = 'InternalValidated',
  reverseWireValidated = 'ReverseWireValidated',
  extendedValidated = 'ExtendedValidated',
  crowdKnowledge = 'CrowdKnowledge',
  externalBankValidated = 'ExternalBankValidated',
  lyonsValidated = 'LyonsValidated',
}

export const validationTypes = {
  [VerificationResultType.extendedValidated]: {
    logo: ExtendedValidationLogo,
    title: 'Extended Validation',
    description:
      'This is the most comprehensive verification. The process entails performing comprehensive validation of a vendor’s legal entity data and bank account information, including identification of the authorized signatories.',
    showDescriptionUnderResult: true,
  },
  [VerificationResultType.internalValidated]: {
    logo: StandardValidationLogo,
    title: 'Standard Validation',
    description:
      'As part of this validation, nsKnox analyzes a bank transfer from this account to verify the name of the account owner, its corporate identity number, as well as its geographic location. This verification ensures that the bank account was registered under the specified name.',
    showDescriptionUnderResult: false,
  },
  [VerificationResultType.selfApproved]: {
    logo: SelfValidationLogo,
    title: 'Self Validation',
    description:
      'Bank account details were validated by your organization due to small transaction amounts or due to internal assessment that the bank account details are correct.',
    showDescriptionUnderResult: false,
  },
  [VerificationResultType.notValidated]: {
    logo: UnknownValidationLogo,
    title: 'Not Validated',
    description: 'This account is not validated.',
    showDescriptionUnderResult: false,
  },
};

export const translateVerificationLevel = (verificationLevel: VerificationResultType): VerificationResultText => {
  switch (verificationLevel) {
    case VerificationResultType.extendedValidated: {
      return VerificationResultText.extendedValidation;
    }
    case VerificationResultType.lightweightValidated:
    case VerificationResultType.internalValidated:
    case VerificationResultType.externalBankValidated:
    case VerificationResultType.reverseWireValidated:
    case VerificationResultType.lyonsValidated: {
      return VerificationResultText.standardValidation;
    }
    case VerificationResultType.selfApproved: {
      return VerificationResultText.selfApproved;
    }
    case VerificationResultType.crowdKnowledge: {
      return VerificationResultText.crowdKnowledge;
    }
    case VerificationResultType.notValidated:
    case VerificationResultType.requireStandardValidation:
    case VerificationResultType.requireExtendedValidation: {
      return VerificationResultText.notValidated;
    }
    default: {
      return VerificationResultText.unknownResult;
    }
  }
};

export function morteeTransformVerifyPayeeAccount(verifyResponse: MorteeVerificationServerResponse): MorteeVerificationResult {
  return {
    data: verifyResponse.lastValidatedPayeeAccountEventData,
    result: verifyResponse.validationData.result,
    privateAccountUniformId: verifyResponse.privatePayeeAccount?.uniformId,
    validatedAccountUniformId: verifyResponse.validatedPayeeAccount?.uniformId,
    validatedPayeeLegalIdentifiers: verifyResponse.aggregatedValidatedPayeeData?.legalIdentifiers,
    strongLegalIdentifiers: verifyResponse.aggregatedValidatedPayeeData?.strongLegalIdentifies,
    primaryFields: verifyResponse.aggregatedValidatedPayeeData?.primaryFields ?? null,
    verificationAccountDetails: verifyResponse.validatedPayeeAccount
      ? {
          ...verifyResponse.validatedPayeeAccount.accountDetails,
          localFormat: verifyResponse?.validatedPayeeAccount?.localFormat,
        }
      : verifyResponse.privatePayeeAccount?.accountDetails,
    validatedAccountAttachments: verifyResponse.validatedPayeeAccount?.attachments,
    sanctionsScreeningInfo: verifyResponse.validatedPayee?.latestSanctionsScreeningEvent,
    verificationTimestamp: verifyResponse.validatedPayeeAccount?.lastValidationTimestamp,
    presentationAccountNumber: verifyResponse.validatedPayeeAccount?.presentationAccountNumber,
  };
}

export function morteeTransformSearchPayeeAccount(
  verifyResponse: SearchServerResponse,
  status?: SearchStatusType,
): SergeeSearchResult {
  return {
    id: verifyResponse.id,
    validatedTimestamp: verifyResponse.validatedTimestamp,
    createTimestamp: verifyResponse.createTimestamp,
    searchRequest: verifyResponse.searchRequest,
    score: verifyResponse.score,
    status: status ?? verifyResponse.status,
    metricsResult: transformMetricsResult(verifyResponse.metricsResult),
    quickCheckId: verifyResponse.quickCheckId,
    validatedPayeeUniformId: verifyResponse.validatedPayeeUniformId,
    validatingUser: verifyResponse.validatingUser,
    knoxId: verifyResponse.knoxId,
    matchingValidationRecords: verifyResponse.matchingValidationRecords ?? [],
  };
}

export function transformMetricsResult(metricsResult: PayeeMetricsResults): Partial<UIMetricsResult> {
  const result = {
    taxId: {
      value: metricsResult.additionalTaxIds?.length
        ? [metricsResult.taxId.value, ...metricsResult.additionalTaxIds]
        : [metricsResult.taxId.value],
      result: metricsResult.taxId.result,
    },
    bankCode: { value: [metricsResult.bankCode.value], result: metricsResult.bankCode.result },
    branchCode: { value: [metricsResult.branchCode.value], result: metricsResult.branchCode.result },
    address: {
      value: metricsResult.additionalAddress?.length
        ? [metricsResult.address.value, ...metricsResult.additionalAddress]
        : [metricsResult.address.value],
      result: metricsResult.address.result,
    },
    additionalNames: { value: metricsResult.additionalNames, result: SergeeMetricResult.Match },
    additionalIds: { value: metricsResult.additionalIds, result: SergeeMetricResult.Match },
    name: { value: [metricsResult.name.value], result: metricsResult.name.result },
    countryCode: {
      value: metricsResult.countryCode?.value ? [metricsResult.countryCode.value] : [],
      result: metricsResult.countryCode?.result ?? SergeeMetricResult.Unknown,
    },
    accountNumber: { value: [metricsResult.accountNumber?.value], result: metricsResult.accountNumber?.result },
    swiftCode: { value: [metricsResult.swiftCode?.value], result: metricsResult.swiftCode?.result },
    duns: {
      value: metricsResult.duns?.value ? [metricsResult.duns.value] : [],
      result: metricsResult.duns?.result ?? SergeeMetricResult.Unknown,
    },
    iban: {
      value: metricsResult.iban?.value ? [metricsResult.iban.value] : [],
      result: metricsResult.iban?.result ?? SergeeMetricResult.Unknown,
    },
    accountCountry: {
      value: metricsResult.accountCountry?.value ? [metricsResult.accountCountry.value] : [],
      result: metricsResult.accountCountry?.result ?? SergeeMetricResult.Unknown,
    },
    payeeCountry: {
      value: metricsResult.payeeCountry?.value ? [metricsResult.payeeCountry.value] : [],
      result: metricsResult.payeeCountry?.result ?? SergeeMetricResult.Unknown,
    },
    bankName: {
      value: metricsResult.bankName?.value ? [metricsResult.bankName.value] : [],
      result: metricsResult.bankName?.result ?? SergeeMetricResult.Unknown,
    },
  };
  removeAdditionalEmptyValues(result);
  return result;
}

export function removeAdditionalEmptyValues(result: Partial<UIMetricsResult>): void {
  if (result.additionalNames?.value.length === 0) {
    delete result.additionalNames;
  }
  if (result.additionalIds?.value.length === 0) {
    delete result.additionalIds;
  }
  if (result.countryCode?.value.length === 0) {
    delete result.countryCode;
  }
  if (result.payeeCountry?.value?.[0] === result.accountCountry?.value?.[0]) {
    delete result.accountCountry;
  }
}

export const defaultMetricsResult: PayeeMetricsResults = {
  name: { value: '', result: SergeeMetricResult.Unknown },
  countryCode: { value: '', result: SergeeMetricResult.Unknown },
  taxId: { value: '', result: SergeeMetricResult.Unknown },
  bankCode: { value: '', result: SergeeMetricResult.Unknown },
  branchCode: { value: '', result: SergeeMetricResult.Unknown },
  accountNumber: { value: '', result: SergeeMetricResult.Unknown },
  address: { value: '', result: SergeeMetricResult.Unknown },
  swiftCode: { value: '', result: SergeeMetricResult.Unknown },
  duns: { value: '', result: SergeeMetricResult.DoNotShow },
  iban: { value: '', result: SergeeMetricResult.Unknown },
  accountCountry: { value: '', result: SergeeMetricResult.Unknown },
  payeeCountry: { value: '', result: SergeeMetricResult.Unknown },
  additionalAddress: [],
  additionalNames: [],
  additionalTaxIds: [],
  additionalIds: [],
  bankName: { value: '', result: SergeeMetricResult.DoNotShow },
};

export interface ValidationEmailToCustomerValuesServerResponse {
  to: string[] | null | undefined;
  referringCustomerName: string | null | undefined;
}

export interface ValidationEmailToCustomerValues {
  to: string[] | undefined;
  referringCustomerName: string | undefined;
}

export function transformToCustomerValidationEmailValues(
  serverResponse: ValidationEmailToCustomerValuesServerResponse,
): ValidationEmailToCustomerValues {
  return {
    to: serverResponse.to ?? undefined,
    referringCustomerName: serverResponse.referringCustomerName ?? undefined,
  };
}

export interface SendValidationEmailToPayeeRequest {
  to: string[];
}

export interface SendValidationEmailToCustomerRequest {
  to: string[];
  referringCustomerName: string;
}
